import { cached, tracked } from '@glimmer/tracking';
import {
  didCancel,
  keepLatestTask,
  restartableTask,
  timeout,
} from 'ember-concurrency';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { addDays } from 'date-fns/addDays';
import { subDays } from 'date-fns/subDays';
import { waitFor } from '@ember/test-waiters';
import { parseISO } from 'date-fns/parseISO';
import SidebarController from 'uplisting-frontend/pods/-sidebar/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { formatLocalDate } from 'uplisting-frontend/utils/date';
import { toggleValue } from 'uplisting-frontend/utils/toggle-value';
import { fromPx, getCssVariable } from 'uplisting-frontend/utils/css';
import { isAnyFocused } from 'uplisting-frontend/utils/active-focus';
import { storage, StorageKey } from 'uplisting-frontend/utils/storage/api';
import {
  ALL_CALENDAR_PARTS,
  CalendarPart,
} from 'uplisting-frontend/services/repositories/calendar';

export enum RowOption {
  // default row
  properties = 'properties',

  rates = 'rates',
  airbnbRates = 'airbnbRates',
  directRates = 'directRates',
  bookingDotComRates = 'bookingDotComRates',
  googleRates = 'googleRates',
  homeAwayRates = 'homeAwayRates',

  mlos = 'mlos',
  closedForArrivals = 'closedForArrivals',
  closedForDepartures = 'closedForDepartures',

  changeovers = 'changeovers',

  multiUnits = 'multiUnits',
}

interface IRowOption {
  id: RowOption;
  text: string;
}

interface IRowGroup {
  group: string;
  rows: IRowOption[];
}

const getterToRow = {
  showMultiUnits: RowOption.multiUnits,
  showChangeovers: RowOption.changeovers,
  showMLOS: RowOption.mlos,
  showClosedForArrivals: RowOption.closedForArrivals,
  showClosedForDepartures: RowOption.closedForDepartures,
  showRates: RowOption.rates,
  showAirbnbRates: RowOption.airbnbRates,
  showDirectRates: RowOption.directRates,
  showBookingDotComRates: RowOption.bookingDotComRates,
  showGoogleRates: RowOption.googleRates,
  showVrboRates: RowOption.homeAwayRates,
};

const BIG_CEIL_HEIGHT = fromPx(getCssVariable('--cell-height-normal'));
const SMALL_CEIL_HEIGHT = fromPx(getCssVariable('--cell-height-small'));

const BATCH_SIZE = 20;
const PREFETCH = 5;
const DATE_CHANGE_DEBOUNCE = 250;

interface IRowRange {
  start: number;
  end: number;
}

interface IFetchDataOptions {
  isInitialRequest?: boolean;
  debounce?: number;
}

interface IRowsRequest {
  ids: string[];
  parts: CalendarPart[];
}

const FIRST_BATCH: IRowRange = { start: 0, end: BATCH_SIZE };

export default class CalendarController extends SidebarController {
  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  baseRoute = 'xcalendar';

  datesOnScreen?: number;

  @tracked selectedRowIds!: RowOption[];

  @tracked loadingDataFor: string[] = [];
  @tracked loadingRatesFor: string[] = [];

  loadedParts = new Map<string, Set<CalendarPart>>();

  #rowsInView = FIRST_BATCH;
  #hasTasks = false;
  #triedTasks = false;
  #hasLoadError = false;
  #loadedFrom?: string;

  @cached
  get fromAsDate(): Date {
    return parseISO(this.from);
  }

  @cached
  get startDate(): Date {
    return subDays(this.fromAsDate, 1);
  }

  @cached
  get defaultRowIds(): RowOption[] {
    if (this.currentUser.hasAccessTo('rates.manage')) {
      return [RowOption.rates, RowOption.airbnbRates, RowOption.mlos];
    }

    return [RowOption.changeovers, RowOption.multiUnits];
  }

  @cached
  get rowGroups(): IRowGroup[] {
    const { intl, currentUser } = this;

    const groups: IRowGroup[] = [];

    if (currentUser.hasAccessTo('rates.manage')) {
      groups.push({
        group: intl.t('calendar.rows.rates.group'),
        rows: [
          {
            id: RowOption.rates,
            text: intl.t('calendar.rows.rates.items.spot_rates'),
          },
          {
            id: RowOption.airbnbRates,
            text: intl.t('calendar.rows.rates.items.airbnb_rates'),
          },
          {
            id: RowOption.directRates,
            text: intl.t('calendar.rows.rates.items.direct_rates'),
          },
          {
            id: RowOption.bookingDotComRates,
            text: intl.t('calendar.rows.rates.items.bdc_rates'),
          },
          {
            id: RowOption.googleRates,
            text: intl.t('calendar.rows.rates.items.google_rates'),
          },
          {
            id: RowOption.homeAwayRates,
            text: intl.t('calendar.rows.rates.items.vrbo_rates'),
          },
        ],
      });
    }

    if (currentUser.hasAccessTo('restrictions.manage')) {
      groups.push({
        group: intl.t('calendar.rows.restrictions.group'),
        rows: [
          {
            id: RowOption.mlos,
            text: intl.t('calendar.rows.restrictions.items.mlos'),
          },
          {
            id: RowOption.closedForArrivals,
            text: intl.t(
              'calendar.rows.restrictions.items.closed_for_arrivals',
            ),
          },
          {
            id: RowOption.closedForDepartures,
            text: intl.t(
              'calendar.rows.restrictions.items.closed_for_departures',
            ),
          },
        ],
      });
    }

    if (currentUser.hasAccessTo('changeover.view')) {
      groups.push({
        group: intl.t('calendar.rows.tasks.group'),
        rows: [
          {
            id: RowOption.changeovers,
            text: intl.t('calendar.rows.tasks.items.changeovers'),
          },
        ],
      });
    }

    groups.push({
      group: intl.t('calendar.rows.multi_units.group'),
      rows: [
        {
          id: RowOption.multiUnits,
          text: intl.t('calendar.rows.multi_units.items.multi_units'),
        },
      ],
    });

    return groups;
  }

  @cached
  get hiddenRowsCount(): number {
    const visibleRowIds = this.rowGroups
      .flatMap((group) => group.rows)
      .map((row) => row.id);

    return visibleRowIds.filter((id) => !this.selectedRowIds.includes(id))
      .length;
  }

  @cached
  get showRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.rates)
    );
  }

  @cached
  get showAirbnbRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.airbnbRates)
    );
  }

  @cached
  get showDirectRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.directRates)
    );
  }

  @cached
  get showBookingDotComRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.bookingDotComRates)
    );
  }

  @cached
  get showGoogleRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.googleRates)
    );
  }

  @cached
  get showVrboRates(): boolean {
    return (
      this.currentUser.hasAccessTo('rates.manage') &&
      this.selectedRowIds.includes(RowOption.homeAwayRates)
    );
  }

  @cached
  get showMLOS(): boolean {
    return (
      this.currentUser.hasAccessTo('restrictions.manage') &&
      this.selectedRowIds.includes(RowOption.mlos)
    );
  }

  @cached
  get showClosedForArrivals(): boolean {
    return (
      this.currentUser.hasAccessTo('restrictions.manage') &&
      this.selectedRowIds.includes(RowOption.closedForArrivals)
    );
  }

  @cached
  get showClosedForDepartures(): boolean {
    return (
      this.currentUser.hasAccessTo('restrictions.manage') &&
      this.selectedRowIds.includes(RowOption.closedForDepartures)
    );
  }

  @cached
  get showChangeovers(): boolean {
    return (
      this.currentUser.hasAccessTo('changeover.view') &&
      this.selectedRowIds.includes(RowOption.changeovers)
    );
  }

  @cached
  get showMultiUnits(): boolean {
    return this.selectedRowIds.includes(RowOption.multiUnits);
  }

  @cached
  get fieldsToShow(): RowOption[] {
    const entries = Object.entries(getterToRow);
    const result: RowOption[] = [];

    for (const [getter, row] of entries) {
      if (this[getter]) {
        result.push(row);
      }
    }

    return result;
  }

  @cached
  get defaultRowHeight(): number {
    const { fieldsToShow } = this;
    const { length } = fieldsToShow;
    const rowsWithoutMultiUnits = fieldsToShow.includes(RowOption.multiUnits)
      ? length - 1
      : length;

    const smallRowsHeight = rowsWithoutMultiUnits * SMALL_CEIL_HEIGHT;

    return BIG_CEIL_HEIGHT + smallRowsHeight;
  }

  @cached
  get filteredProperties(): ModelRegistry['property'][] {
    const properties = this.selectedItems.length
      ? this.selectedProperties
      : this.properties;

    return properties
      .filter((property) => property.id)
      .sort((x, y) => x.order - y.order);
  }

  @action
  handleLogout(): void {
    this.session.handleLogout();
  }

  @action
  handleOpenCalendar(): void {
    if (isAnyFocused()) {
      return;
    }

    this.router.transitionTo(this.baseRoute);

    this.calendarManager.interaction.resetEventOperation();
    this.calendarManager.interaction.resetDragOperation();
    this.calendarManager.interaction.resetResizeOperation();
  }

  @action
  handleCalendarResize(): void {
    this.calendarManager.handleResize();
  }

  @action
  handleSelectRow(value: IRowOption): void {
    toggleValue(this, 'selectedRowIds', value.id);

    storage.local.set(StorageKey.CALENDAR_ROWS, this.selectedRowIds.join(','));
  }

  @action
  @waitFor
  async handleSelectFrom(date: Date): Promise<boolean> {
    this.from = formatLocalDate(date);

    try {
      await this.fetchDataTask.perform({ debounce: DATE_CHANGE_DEBOUNCE });

      return true;
    } catch (e) {
      if (!didCancel(e)) {
        if (this.#loadedFrom) {
          this.from = this.#loadedFrom;
        }

        this.#showLoadError();
      }

      return false;
    }
  }

  @action
  handleGoToToday(): void {
    const date = this.calendarManager.utils.getNavigationDates().today;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadPrevRange(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).previousRange;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadPrevWeek(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).previousWeek;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadNextWeek(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).nextWeek;

    this.handleSelectFrom(date);
  }

  @action
  handleLoadNextRange(): void {
    const date = this.calendarManager.utils.getNavigationDates(
      this.fromAsDate,
    ).nextRange;

    this.handleSelectFrom(date);
  }

  @action
  @waitFor
  async handleFirstVisibleChange(
    _record: ModelRegistry['property'],
    index: number,
  ): Promise<void> {
    this.#rowsInView = { ...this.#rowsInView, start: index };

    await this.loadRowsInView();
  }

  @action
  @waitFor
  async handleLastVisibleChange(
    _record: ModelRegistry['property'],
    index: number,
  ): Promise<void> {
    this.#rowsInView = { ...this.#rowsInView, end: index + 1 + PREFETCH };

    await this.loadRowsInView();
  }

  fetchDataTask = restartableTask(
    async ({
      isInitialRequest = false,
      debounce = 0,
    }: IFetchDataOptions = {}) => {
      this.loadRowsInViewTask.cancelAll();

      if (isInitialRequest) {
        this.selectedRowIds = this.storedRowIds();
      }

      if (debounce) {
        await timeout(debounce);
      }

      const { from, startDate } = this;

      const requests = this.#requestsFor(
        this.#idsBetween(this.#rowsInView),
        new Map(),
      );

      const request = new window.AbortController();

      this.#triedTasks = this.showsTasks();

      try {
        const [payloads, hasTasks] = await Promise.all([
          this.#fetchRows(startDate, requests, request.signal),
          this.showsTasks()
            ? this.calendarManager.repository.loadTasks(
                startDate,
                this.#endDate(startDate),
              )
            : false,
        ]);

        this.calendarManager.replaceData(
          startDate,
          payloads,
          this.datesOnScreen,
        );

        this.#loadedFrom = from;
        this.loadedParts = new Map();
        this.#markLoaded(requests);
        this.#hasTasks = hasTasks;
        this.#hasLoadError = false;
      } finally {
        request.abort();
      }

      void this.loadRowsInView();
    },
  );

  loadRowsInViewTask = keepLatestTask(async () => {
    await timeout(0);

    // rows load for the dates on screen, which a pending date change runs ahead of
    const startDate = this.calendarManager.currentStartDate;

    if (this.fetchDataTask.isRunning || !startDate) {
      return;
    }

    const requests = this.#requestsToLoad();
    const withTasks = this.showsTasks() && !this.#hasTasks;

    // tasks turned on later load alone; a failed load waits for the next rows
    if (requests.length === 0 && (!withTasks || this.#triedTasks)) {
      return;
    }

    this.#triedTasks ||= withTasks;

    const request = new window.AbortController();

    this.loadingDataFor = this.#idsLoading(requests, CalendarPart.occasions);
    this.loadingRatesFor = this.#idsLoading(requests, CalendarPart.rates);

    try {
      const [payloads, hasNewTasks] = await Promise.all([
        this.#fetchRows(startDate, requests, request.signal),
        withTasks
          ? this.calendarManager.repository.loadTasks(
              startDate,
              this.#endDate(startDate),
            )
          : false,
      ]);

      this.calendarManager.addData(
        payloads,
        requests.flatMap(({ parts }) => parts),
      );

      this.#markLoaded(requests);
      this.#hasTasks ||= hasNewTasks;
      this.#hasLoadError = false;
    } finally {
      request.abort();

      this.loadingDataFor = [];
      this.loadingRatesFor = [];
    }
  });

  public resetState(): void {
    this.fetchDataTask.cancelAll();
    this.loadRowsInViewTask.cancelAll();

    this.loadedParts = new Map();
    this.#hasTasks = false;
    this.#triedTasks = false;
    this.#hasLoadError = false;
    this.#loadedFrom = undefined;
    this.#rowsInView = FIRST_BATCH;

    this.calendarManager.data.resetState();
  }

  public storedRowIds(): RowOption[] {
    const rows = storage.local.get<string>(StorageKey.CALENDAR_ROWS);

    return rows === null
      ? this.defaultRowIds
      : (rows.split(',').filter(Boolean) as RowOption[]);
  }

  @waitFor
  public async loadRowsInView(): Promise<void> {
    try {
      await this.loadRowsInViewTask.perform();
    } catch (e) {
      if (!didCancel(e)) {
        this.#showLoadError();
      }
    }
  }

  public partsShownFor(ids: string[]): Map<string, CalendarPart[]> {
    return new Map(ids.map((id) => [id, ALL_CALENDAR_PARTS]));
  }

  public showsTasks(): boolean {
    return false;
  }

  #showLoadError(): void {
    if (!this.#hasLoadError) {
      this.notifications.error();
    }

    this.#hasLoadError = true;
  }

  #fetchRows(
    startDate: Date,
    requests: IRowsRequest[],
    signal: AbortSignal,
  ): Promise<object[]> {
    const endDate = this.#endDate(startDate);

    return Promise.all(
      requests.map(({ ids, parts }) =>
        this.calendarManager.repository.fetchData(
          { startDate, endDate, ids: ids.join(','), parts },
          signal,
        ),
      ),
    );
  }

  #endDate(startDate: Date): Date | undefined {
    return this.datesOnScreen
      ? addDays(startDate, this.datesOnScreen - 1)
      : undefined;
  }

  #requestsFor(ids: string[], loaded = this.loadedParts): IRowsRequest[] {
    const shown = this.partsShownFor(ids);
    const requests = new Map<string, IRowsRequest>();

    for (const id of ids) {
      const loadedForId = loaded.get(id);
      const parts = (shown.get(id) ?? []).filter(
        (part) => !loadedForId?.has(part),
      );

      if (parts.length === 0) {
        continue;
      }

      const key = parts.join();
      const request = requests.get(key) ?? { ids: [], parts };

      request.ids.push(id);
      requests.set(key, request);
    }

    return [...requests.values()];
  }

  #markLoaded(requests: IRowsRequest[]): void {
    for (const { ids, parts } of requests) {
      for (const id of ids) {
        const loaded = this.loadedParts.get(id) ?? new Set();

        for (const part of parts) {
          loaded.add(part);
        }

        this.loadedParts.set(id, loaded);
      }
    }
  }

  #idsLoading(requests: IRowsRequest[], part: CalendarPart): string[] {
    return requests
      .filter(({ parts }) => parts.includes(part))
      .flatMap(({ ids }) => ids);
  }

  #requestsToLoad(): IRowsRequest[] {
    const { start, end } = this.#rowsInView;
    const batchStart = Math.floor(start / BATCH_SIZE) * BATCH_SIZE;

    const requests = this.#requestsFor(
      this.#idsBetween({
        start: batchStart,
        end: Math.ceil(end / BATCH_SIZE) * BATCH_SIZE,
      }),
    );

    const inView = new Set(this.#idsBetween(this.#rowsInView));

    return requests.some(({ ids }) => ids.some((id) => inView.has(id)))
      ? requests
      : [];
  }

  #idsBetween({ start, end }: IRowRange): string[] {
    return this.filteredProperties
      .slice(start, end)
      .map((property) => property.id);
  }
}
